import React from 'react'
import { Alert, Button, Empty, Flex, Popconfirm, Select, Table } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../auth'
import { useClients, useAssignClientCampaign, useUnassignClientCampaign } from '../../../client/hooks'
import { Client } from '../../../client/client'
import { UserAvatar } from '../../../user/components/user-avatar'
import { MailLink } from '../../../components/mail-link'
import { ASSIGN_CLIENT_TO_CAMPAIGN, INDEX_CLIENT, UNASSIGN_CLIENT_FROM_CAMPAIGN } from '../../../user/permission'

type CampaignClientsSectionProps = {
    campaignId: string
}

export function CampaignClientsSection({ campaignId }: CampaignClientsSectionProps) {
    const { t } = useTranslation()
    const { activeCompany, can } = useAuth()
    const clientsQuery = useClients(activeCompany?.id)

    const canAssign = can(ASSIGN_CLIENT_TO_CAMPAIGN)
    const canUnassign = can(UNASSIGN_CLIENT_FROM_CAMPAIGN)

    if (!can(INDEX_CLIENT)) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t('You do not have permission to manage clients on this campaign.')}
            />
        )
    }

    const allClients = clientsQuery.data ?? []
    const assignedClients = allClients.filter((client) =>
        client.assignedCampaigns.some((campaign) => campaign.id === campaignId)
    )
    const unassignedClients = allClients.filter(
        (client) => !client.assignedCampaigns.some((campaign) => campaign.id === campaignId)
    )

    return (
        <Flex
            vertical
            gap="middle"
        >
            {canAssign && (
                <AddClientSelect
                    campaignId={campaignId}
                    candidates={unassignedClients}
                />
            )}
            <Table<Client>
                size="small"
                rowKey="userId"
                loading={clientsQuery.isPending}
                locale={{
                    emptyText: <Empty description={t('No clients with access yet')} />,
                }}
                dataSource={assignedClients}
                pagination={false}
                columns={[
                    {
                        title: '',
                        dataIndex: 'avatar',
                        width: 60,
                        render(text) {
                            return <UserAvatar user={{ avatar: text }} />
                        },
                    },
                    { title: t('Name'), dataIndex: 'name' },
                    { title: t('Email'), dataIndex: 'email', render: (email) => <MailLink email={email} /> },
                    {
                        key: 'action',
                        width: 50,
                        hidden: !canUnassign,
                        render: (_, record) => (
                            <Flex justify="end">
                                <RemoveAccessButton
                                    client={record}
                                    campaignId={campaignId}
                                />
                            </Flex>
                        ),
                    },
                ]}
            />
        </Flex>
    )
}

type AddClientSelectProps = {
    campaignId: string
    candidates: Client[]
}

function AddClientSelect({ campaignId, candidates }: AddClientSelectProps) {
    const { t } = useTranslation()
    const { activeCompany } = useAuth()
    const [selectedUserId, setSelectedUserId] = React.useState<string | undefined>(undefined)
    const assignMutation = useAssignClientCampaign(activeCompany?.id, selectedUserId)

    const options = candidates.map((client) => ({
        value: client.userId,
        label: client.name || client.email || client.userId,
    }))

    const handleChange = (userId: string) => {
        setSelectedUserId(userId)
    }

    const handleAdd = () => {
        if (!selectedUserId) {
            return
        }

        assignMutation.mutate(campaignId, {
            onSuccess: () => setSelectedUserId(undefined),
        })
    }

    return (
        <Flex
            gap="small"
            className="flex-col sm:flex-row"
        >
            <Select<string>
                showSearch
                allowClear
                value={selectedUserId}
                onChange={handleChange}
                options={options}
                placeholder={t('Select a client to give access')}
                optionFilterProp="label"
                className="min-w-0 w-full flex-1"
                disabled={candidates.length === 0}
            />
            <Button
                type="primary"
                onClick={handleAdd}
                disabled={!selectedUserId}
                loading={assignMutation.isPending}
            >
                {t('Give access')}
            </Button>
        </Flex>
    )
}

type RemoveAccessButtonProps = {
    client: Client
    campaignId: string
}

function RemoveAccessButton({ client, campaignId }: RemoveAccessButtonProps) {
    const { t } = useTranslation()
    const unassignMutation = useUnassignClientCampaign(client.companyId, client.userId)

    return (
        <Popconfirm
            title={t('Remove access for this client?')}
            onConfirm={() => unassignMutation.mutate(campaignId)}
        >
            <Button
                type="text"
                aria-label={t('Remove access')}
                title={t('Remove access')}
                loading={unassignMutation.isPending}
                icon={<DeleteOutlined />}
            />
        </Popconfirm>
    )
}
